import { createStore, type StoreApi } from 'zustand/vanilla';

/**
 * Framework-agnostic auth-modal state core.
 *
 * The auth-modal state used to live in `useState` hooks inside `AuthModalProvider`, which chained it
 * to the React tree. It now lives in a Zustand vanilla store so the state can be read and written
 * without React — the prerequisite for Astro islands driving the auth modal (NGF-6331), mirroring the
 * betslip migration (`packages/features/betslip/src/store/betslip-store.ts`, NGF-6116).
 *
 * `AuthModalProvider` is a thin adapter over this core; cross-island / non-React consumers read and
 * write it via the module pointer below. The store exposes Zustand's standard `StoreApi` directly —
 * read with `.getState()`, write with `.setState({ field })`, subscribe with `.subscribe(listener)` —
 * with small pure action helpers (`openAuthModalAtView` / `closeAuthModal` / `notifyAuthLogin`) so the
 * React adapter, the legacy bridge and Astro islands share one open/close/login implementation.
 */

export const VIEW_FORGOT_PASSWORD = 'forgot-password' as const;
export const VIEW_LOGIN = 'login' as const;
export const VIEW_SIGNUP = 'signup' as const;
export const VIEW_VERIFY_EMAIL = 'verify-email' as const;
export const VIEW_VERIFY_PAYMENT = 'verify-payment' as const;

export type View =
  | typeof VIEW_FORGOT_PASSWORD
  | typeof VIEW_LOGIN
  | typeof VIEW_SIGNUP
  | typeof VIEW_VERIFY_EMAIL
  | typeof VIEW_VERIFY_PAYMENT;

export type OpenAtViewOptions = {
  // Seeds the signup form's email field. Threaded into SignupForm by the React adapter; applied to
  // the legacy OC widget via DOM pre-fill by the legacy bridge.
  prefillEmail?: string;
};

export type AuthModalStoreState = {
  isOpen: boolean;
  // Monotonic counter bumped whenever a login succeeds during this session (by the legacy bridge on
  // `OC:LoggedIn`). Consumers watch it for optimistic post-login UI instead of listening to `OC:*`
  // events directly, keeping the store the single source of truth.
  loginNonce: number;
  prefillEmail?: string;
  view: View;
};

export type AuthModalStore = StoreApi<AuthModalStoreState>;

export const createAuthModalStore = (init: Partial<AuthModalStoreState> = {}): AuthModalStore =>
  createStore<AuthModalStoreState>(() => ({
    isOpen: false,
    loginNonce: 0,
    prefillEmail: undefined,
    view: VIEW_LOGIN,
    ...init,
  }));

export const openAuthModalAtView = (store: AuthModalStore, view: View, options: OpenAtViewOptions = {}): void => {
  store.setState({ isOpen: true, prefillEmail: options.prefillEmail, view });
};

export const closeAuthModal = (store: AuthModalStore): void => {
  store.setState({ isOpen: false, prefillEmail: undefined });
};

export const notifyAuthLogin = (store: AuthModalStore): void => {
  store.setState({ loginNonce: store.getState().loginNonce + 1 });
};

// Pointer to the live store on the client, where exactly one AuthModalProvider is mounted. Lets
// non-React consumers (Astro islands, the legacy bridge) read and write the core directly.
let activeAuthModalStore: AuthModalStore | null = null;

// Listeners notified when the active-store *pointer* changes (not state within a store). Fired when
// AuthModalProvider mounts (publishes its store) or unmounts (clears it). Lets a consumer that renders
// in a separate React root react to the store becoming available *after* its own first render — the
// Astro island hydration ordering, where a consumer island can hydrate before the owner publishes.
const activeStoreListeners = new Set<() => void>();

export const setActiveAuthModalStore = (store: AuthModalStore | null): void => {
  // Client-only by contract: the pointer exists for browser consumers, and guarding here keeps any
  // future server-side caller from leaking one request's store into another.
  if (typeof window === 'undefined') return;
  activeAuthModalStore = store;
  activeStoreListeners.forEach(listener => listener());
};

export const getActiveAuthModalStore = (): AuthModalStore | null => activeAuthModalStore;

/**
 * Subscribe to changes of the active-store pointer itself (publish/clear), not to state within a
 * store. Returns an unsubscribe. Designed for `useSyncExternalStore`: pair it with
 * `getActiveAuthModalStore` so a consumer re-renders — and can re-subscribe to the new store — the
 * moment a store is published after that consumer first rendered.
 */
export const subscribeToActiveAuthModalStore = (listener: () => void): (() => void) => {
  activeStoreListeners.add(listener);
  return () => activeStoreListeners.delete(listener);
};
